import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import posthog from 'posthog-js';

const MAX_PIX_KEYS = 6;

const PixAdd = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [pixKey, setPixKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentKeysCount, setCurrentKeysCount] = useState(0);

  useEffect(() => {
    if (!user?.id) return;

    const fetchKeysCount = async () => {
      try {
        const response = await api.get(`/bank/pix/${user.id}`);
        const keys = response.data?.keys || response.data || [];
        setCurrentKeysCount(Array.isArray(keys) ? keys.length : 0);
      } catch (err) {
        console.warn('Não foi possível verificar o número de chaves PIX:', err);
      }
    };

    fetchKeysCount();
  }, [user?.id]);

  const hasReachedLimit = currentKeysCount >= MAX_PIX_KEYS;
  const isValid = pixKey.trim().length > 0 && !hasReachedLimit;

  const handleInputChange = (e) => {
    const value = e.target.value.slice(0, 32);
    setPixKey(value);
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (hasReachedLimit) {
      setError(`Limite máximo de ${MAX_PIX_KEYS} chaves PIX atingido. Remova uma chave para adicionar uma nova.`);
      return;
    }

    if (!isValid || loading) {
      if (!pixKey.trim()) {
        setError('Informe uma chave PIX.');
      }
      return;
    }

    if (!user?.id) {
      setError('Não foi possível identificar o usuário.');
      return;
    }

    const finalKey = pixKey.trim();

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await api.post('/bank/pix', {
        userId: user.id,
        pixKey: finalKey,
      });

      posthog.capture('pix_key_added');
      setSuccess('Chave PIX adicionada com sucesso.');
      setPixKey('');

      setTimeout(() => {
        navigate('/bank/pix');
      }, 1200);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Não foi possível adicionar a chave PIX.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="account-section" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />
          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="text-sm font-semibold flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <i className="bi-arrow-left-short text-xl"></i> Voltar
            </button>

            <InternalTopMenu onLogout={logout} />
          </header>

          <section className="max-w-xl mx-auto">
            <div className="rounded-3xl border border-slate-200 bg-white px-6 py-8 shadow-sm">
              <h1 className="text-xl font-semibold text-slate-900 mb-6">Adicionar chave PIX</h1>

              {hasReachedLimit && (
                <div className="mb-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  Você atingiu o limite máximo de {MAX_PIX_KEYS} chaves PIX. Remova uma chave existente para adicionar uma nova.
                </div>
              )}

              {error && (
                <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
              )}

              {success && (
                <div className="mb-4 rounded border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  {success}
                </div>
              )}

              <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
                <div>
                  <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 focus-within:border-slate-400">
                    <i className="bi-key text-slate-400 text-lg"></i>
                    <input
                      type="text"
                      value={pixKey}
                      onChange={handleInputChange}
                      placeholder="Chave PIX"
                      className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                      maxLength={32}
                      disabled={hasReachedLimit}
                    />
                  </div>
                  {hasReachedLimit && (
                    <p className="mt-2 text-xs text-amber-600 font-semibold">
                      Limite máximo de {MAX_PIX_KEYS} chaves PIX atingido. Remova uma chave existente para adicionar uma nova.
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!isValid || loading || hasReachedLimit}
                  className={`mt-4 inline-flex items-center justify-center rounded-full px-6 py-2 text-sm font-semibold transition-colors ${
                    !isValid || loading || hasReachedLimit
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-tBlue text-white hover:bg-tBlue2'
                  }`}
                >
                  {loading ? 'Adicionando...' : hasReachedLimit ? 'Limite atingido' : 'Adicionar PIX'}
                </button>
              </form>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default PixAdd;

